'use client';

import React, { useEffect, useRef, useState } from 'react';
import { BenzingaLogo } from '@benzinga/logos-ui';

export interface LearnSubnavLink {
  label: string;
  href: string;
}

export interface LearnSubnavProps {
  links?: LearnSubnavLink[];
  homeHref?: string;
}

const DEFAULT_LEARN_LINKS: LearnSubnavLink[] = [
  { label: 'Cryptocurrency', href: '/learn/cryptocurrency' },
  { label: 'Equities', href: '/learn/equities' },
  { label: 'Prediction Markets', href: '/learn/prediction-markets' },
  { label: 'Debt Management', href: '/learn/debt-management' },
];

const LearnLogo: React.FC = () => (
  <svg className="block h-[22px] w-auto max-[900px]:h-[13px]" viewBox="0 0 432 88" fill="none" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Benzinga Learn">
    <path
      d="M1.68195 4.35328C1.11758 2.89256 0.835397 2.1622 0.962076 1.57692C1.07288 1.06499 1.38008 0.616957 1.81768 0.329103C2.31798 5.21306e-08 3.10095 6.97395e-08 4.6669 1.04957e-07L424 9.53564e-06C426.8 9.59862e-06 428.2 9.6301e-06 429.27 0.544977C430.211 1.02434 430.976 1.78925 431.455 2.73006C432 3.79962 432 5.19975 432 8.00001V80C432 82.8003 432 84.2004 431.455 85.27C430.976 86.2108 430.211 86.9757 429.27 87.455C428.2 88 426.8 88 424 88H39.4854C37.5064 88 36.5169 88 35.6641 87.6784C34.9106 87.3942 34.2369 86.9323 33.7003 86.3318C33.0929 85.6522 32.7363 84.7292 32.0231 82.8832L1.68195 4.35328Z"
      fill="#3EFFDC"
    />
    <path
      d="M59.1282 71V16.4545H72.3118V60.2933H95.0036V71H59.1282ZM124.328 71V16.4545H162.361V27.1612H137.512V38.3473H160.417V49.0806H137.512V60.2933H162.361V71H124.328ZM204.916 71H190.747L209.151 16.4545H226.702L245.106 71H230.937L218.126 30.1974H217.7L204.916 71ZM203.025 49.5334H232.642V59.5476H203.025V49.5334ZM273.479 71V16.4545H296.011C300.095 16.4545 303.619 17.1914 306.584 18.6651C309.567 20.1211 311.867 22.2163 313.482 24.9506C315.098 27.6673 315.906 30.8899 315.906 34.6186C315.906 38.4006 315.08 41.6143 313.429 44.2599C311.778 46.8878 309.434 48.8942 306.398 50.2791C303.362 51.6463 299.766 52.3299 295.611 52.3299H281.362V41.9428H293.161C295.15 41.9428 296.81 41.6854 298.141 41.1705C299.491 40.6378 300.512 39.8388 301.204 38.7734C301.897 37.6903 302.243 36.3054 302.243 34.6186C302.243 32.9318 301.897 31.538 301.204 30.4371C300.512 29.3185 299.491 28.484 298.141 27.9336C296.792 27.3654 295.132 27.0813 293.161 27.0813H286.662V71H273.479ZM304.187 46.071L317.77 71H303.388L290.071 46.071H304.187ZM392.059 16.4545V71H380.873L359.167 39.5192H358.821V71H345.637V16.4545H356.983L378.449 47.8821H378.902V16.4545H392.059Z"
      fill="#192940"
    />
  </svg>
);

export const LearnSubnav: React.FC<LearnSubnavProps> = ({ links = DEFAULT_LEARN_LINKS, homeHref = '/learn' }) => {
  const [stuck, setStuck] = useState(false);
  const sentinelRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(([entry]) => setStuck(!entry.isIntersecting), { threshold: 0 });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <span ref={sentinelRef} className="mb-[-1px] block h-px" aria-hidden="true" />
      <div
        className="group sticky top-0 z-[850] border-b-2 border-[#3effdc] bg-[#0e1a29]"
        data-stuck={stuck || undefined}
      >
        <div className="mx-auto flex h-[52px] max-w-[1400px] items-center gap-8 px-4 max-[900px]:h-10 max-[900px]:gap-0 max-[900px]:px-0">
          <a
            className="inline-flex flex-none items-center max-[900px]:invisible max-[900px]:max-w-0 max-[900px]:overflow-hidden max-[900px]:border-r max-[900px]:border-transparent max-[900px]:opacity-0 max-[900px]:transition-[max-width,margin,padding-right,opacity,visibility] max-[900px]:duration-300 group-data-[stuck]:max-[900px]:visible group-data-[stuck]:max-[900px]:ml-4 group-data-[stuck]:max-[900px]:mr-3 group-data-[stuck]:max-[900px]:max-w-[80px] group-data-[stuck]:max-[900px]:border-[#2a3a4e] group-data-[stuck]:max-[900px]:pr-3 group-data-[stuck]:max-[900px]:opacity-100"
            href={homeHref}
            aria-label="Benzinga Learn — home"
          >
            <span
              className="mr-0 inline-flex max-w-0 items-center overflow-hidden border-r border-transparent pr-0 opacity-0 transition-[max-width,margin-right,padding-right,opacity] duration-300 [&_.benzinga-logo]:h-[18px] [&_.benzinga-logo]:w-auto [&_.benzinga-logo_svg]:h-[18px] [&_.benzinga-logo_svg]:w-auto group-data-[stuck]:mr-3.5 group-data-[stuck]:max-w-[140px] group-data-[stuck]:border-[#2a3a4e] group-data-[stuck]:pr-3.5 group-data-[stuck]:opacity-100 max-[900px]:hidden"
              aria-hidden={!stuck}
            >
              <BenzingaLogo variant="light" />
            </span>
            <LearnLogo />
          </a>
          <nav
            className="flex items-center gap-[26px] max-[900px]:min-w-0 max-[900px]:flex-1 max-[900px]:gap-[22px] max-[900px]:self-stretch max-[900px]:overflow-x-auto max-[900px]:px-4 max-[900px]:overscroll-x-contain max-[900px]:[mask-image:linear-gradient(to_right,#000_calc(100%-28px),transparent)] max-[900px]:[scrollbar-width:none] max-[900px]:[-webkit-overflow-scrolling:touch] max-[900px]:[&::-webkit-scrollbar]:hidden"
            aria-label="Learn"
          >
            {links.map(link => (
              <a
                className="relative whitespace-nowrap text-sm font-semibold text-[#c4d0de] no-underline transition-colors duration-150 after:absolute after:bottom-[-6px] after:left-0 after:right-0 after:h-0.5 after:origin-left after:scale-x-0 after:bg-[#3effdc] after:content-[''] after:transition-transform after:duration-150 hover:text-[#3effdc] hover:after:scale-x-100 max-[900px]:inline-flex max-[900px]:items-center max-[900px]:self-stretch max-[900px]:text-[13px] max-[900px]:after:bottom-1.5"
                href={link.href}
                key={link.href}
              >
                {link.label}
              </a>
            ))}
          </nav>
        </div>
      </div>
    </>
  );
};

export default LearnSubnav;
